<template>
  <div class="pe-rich-text-editor-wrapper">
    <quill-editor ref="editorRef" v-model:value="content" contentType="html" :options="editorOptions"
      @ready="onEditorReady" :disabled="isUploading" />

    <div v-if="isUploading" class="editor-loading-overlay">
      <div class="loading-content">
        <span class="loader"></span>
        <span>Subiendo imagen...</span>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, computed, inject, watch } from "vue";
import { quillEditor } from "vue3-quill";
import "quill/dist/quill.core.css";
import "quill/dist/quill.snow.css";

const props = defineProps({
  modelValue: { type: String, default: "" },
  placeholder: { type: String, default: "" },
  enableImageUpload: { type: Boolean, default: false },
  uploadHandler: { type: Function, default: null },
});

const emit = defineEmits([
  "update:modelValue",
  "upload-error",
  "upload-start",
  "upload-end",
]);

const engine = inject('formEngine', null);

const editorRef = ref(null);
const editorInstance = ref(null);
const isUploading = ref(false);

const content = computed({
  get: () => props.modelValue,
  set: (val) => emit("update:modelValue", val),
});

const activeUploader = computed(() => {
  if (props.uploadHandler) return props.uploadHandler;
  if (engine?.apiAdapter?.uploadFile && typeof engine.apiAdapter.uploadFile === 'function') {
    return engine.apiAdapter.uploadFile;
  }
  return null;
});

// --- VALIDACIÓN DE CAPACIDAD ---
const canUpload = computed(() => props.enableImageUpload && !!activeUploader.value);

const editorOptions = computed(() => {
  const toolbarConfig = [
    [{ header: [1, 2, 3, false] }],
    ["bold", "italic", "underline"],
    [{ list: "ordered" }, { list: "bullet" }],
  ];

  if (canUpload.value) {
    toolbarConfig.push(["link", "image"]);
  } else {
    toolbarConfig.push(["link"]);
  }

  return {
    theme: "snow",
    placeholder: props.placeholder,
    modules: {
      toolbar: toolbarConfig,
    },
  };
});

// --- MANEJO DE EVENTOS ---
const onEditorReady = (quill) => {
  editorInstance.value = quill;

  // Registramos el handler de imagen si corresponde
  if (canUpload.value) {
    const toolbar = quill.getModule("toolbar");
    toolbar.addHandler("image", triggerImageUpload);
  }
};

const triggerImageUpload = () => {
  const input = document.createElement("input");
  input.type = "file";
  input.accept = "image/*";
  input.click();

  input.onchange = async () => {
    const file = input.files[0];
    if (!file) return;

    if (!activeUploader.value) {
      console.error("No upload handler defined");
      return;
    }

    isUploading.value = true;
    emit("upload-start");

    try {
      const response = await activeUploader.value(file);

      const imageUrl = response?.url || response;

      if (imageUrl && editorInstance.value) {
        const quill = editorInstance.value;
        const range = quill.getSelection(true) || { index: quill.getLength() };
        quill.insertEmbed(range.index, "image", imageUrl);
        quill.setSelection(range.index + 1);
      }
    } catch (err) {
      console.error("RichTextEditor upload error:", err);
      emit("upload-error", err.message || file.name);
    } finally {
      isUploading.value = false;
      emit("upload-end");
    }
  };
};
</script>

<style scoped>
.pe-rich-text-editor-wrapper {
  position: relative;
  display: flex;
  flex-direction: column;
  width: 100%;

  /* Variables CSS del Engine */
  background: var(--pe-input-bg, #F5F5F5);
  border: var(--pe-input-border, 1px solid var(--pe-input-border-color, #F5F5F5));
  border-radius: var(--pe-input-border-radius, 0.5rem);

  transition: border-color 0.2s;
  overflow: hidden;
}

/* .pe-rich-text-editor-wrapper:focus-within {
    border-color: var(--pe-primary-color, #00C3A8);
} */

.editor-loading-overlay {
  position: absolute;
  inset: 0;
  background: var(--pe-editor-loading-overlay, rgba(255, 255, 255, 0.8));
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 10;
}

.loading-content {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 10px;
  font-size: 0.8rem;
  color: var(--pe-input-text-color, #333);
}

.loader {
  border: 3px solid #f3f3f3;
  border-top: 3px solid var(--pe-primary-color, #00c3a8);
  border-radius: 50%;
  width: 24px;
  height: 24px;
  animation: spin 1s linear infinite;
}

@keyframes spin {
  0% {
    transform: rotate(0deg);
  }

  100% {
    transform: rotate(360deg);
  }
}

/* --- QUILL OVERRIDES --- */

:deep(.ql-toolbar) {
  border: none !important;
  border-bottom: 1px solid rgba(0, 0, 0, 0.05) !important;
  background-color: transparent !important;
  padding: 8px !important;
}

:deep(.ql-container) {
  border: none !important;
  color: var(--pe-input-text-color, #333);
  min-height: var(--pe-ql-editor-min-height, 100px);
}

:deep(.ql-editor) {
  padding: var(--pe-input-padding, 0.75vw) !important;
  min-height: var(--pe-ql-editor-min-height, 100px);
  text-align: var(--pe-input-text-align, left);
}

:deep(.ql-editor.ql-blank::before) {
  color: #757575;
  right: auto;
  left: var(--pe-input-padding, 0.75vw);
}
</style>